import get from 'lodash/get';

import Asset from './Asset';
import Video from './Video';
import { m3u8Url, highestResolutionMp4Url } from './utils';

export const PLAYLIST_CURATED_TYPE = 0;
export const PLAYLIST_DYNAMIC_TYPE = 1;

/**
 * An embedded assst.
 */
export default class Embed extends Asset {
  /**
   * Create an embed asset.
   * @param {string} id - Embed ID.
   * @param {Object} settings - Settings object.
   */
  constructor(id, settings, defaultAttributes) {
    super(`embeds/${id}`, settings, defaultAttributes);

    this.id = id;
  }

  /**
   * @return {string} The title of the asset.
   */
  get title() {
    return get(this.attributes, 'media.title', '');
  }

  /**
   * @return {string} A description of the video asset.
   */
  get description() {
    return get(this.attributes, 'media.description', '');
  }

  /**
   * @return {string} The preview image URL.
   */
  get poster() {
    return get(this.attributes, 'media.cropped_preview_image', '');
  }

  /**
   * @return {string} ad tag URL, if ads are enabled. empty string otherwise
   */
  get adTag() {
    return get(this.attributes, 'site.ad_tag', '');
  }

  /**
   * @return {boolean} Whether ads are enabled.
   */
  get adsEnabled() {
    return !get(this.attributes, 'ads_disabled', true);
  }

  /**
   * @return {string} Aspect ratio setting. Defaults to false for fluid layouts.
   */
  get aspectRatio() {
    return get(this.attributes, 'aspect_ratio', '16:9');
  }

  /**
   * @return {boolean} Autoplay embed setting.
   */
  get autoplay() {
    return get(this.attributes, 'autoplay', true);
  }

  /**
   * Returns the muted setting.  If we are autoplaying, we should always
   * be muted.
   * @return {boolean} muted embed setting
   */
  get muted() {
    return get(this.attributes, 'mute', true);
  }

  /**
   * @return {string} For single video embeds, the video ID.
   */
  get videoId() {
    return get(this.attributes, 'media.id', '');
  }

  /**
   * @return {string} Video source name, such as 'KTVU'.
   */
  get videoSource() {
    return get(this.attributes, 'media.source.title', '');
  }

  /**
   * @return {string} M3U8 URL.
   */
  get m3u8Url() {
    return m3u8Url(get(this.attributes, 'media.transcodings', []));
  }

  /**
   * @return {string} mp4 URL.
   */
  get mp4Url() {
    return highestResolutionMp4Url(get(this.attributes, 'media.transcodings', []));
  }

  /**
   * Retrieve the type of embed (video or playlist).
   */
  get mediaType() {
    return get(this.attributes, 'media_type');
  }

  /**
   * Return a list of videos in an embed playlist.
   * @return {Video[]} List of video objects.
   */
  get videos() {
    // We are choosing to return a the single video as a playlist if the
    // embed as a single video embed, to match common player behavior
    if (!this.isPlaylist) {
      const media = get(this.attributes, 'media', {});
      return [new Video(media.id, this.settings, media)];
    }

    const videos = get(this.attributes, 'media.videos', []);
    return videos.map(vid => new Video(get(vid, 'id'), this.settings, vid));
  }

  /**
   * return an SRC useable by the player
   */
  get src() {
    const src = [
      {
        src: this.m3u8Url,
        videoId: this.videoId,
        type: 'application/x-mpegURL',
      },
    ];

    // only push in the mp4 URL if it's available
    if (this.mp4Url) {
      src.push({
        src: this.mp4Url,
        videoId: this.videoId,
        type: 'video/mp4',
      });
    }

    return src;
  }

  /**
   * Return the single video (or the first video in a playlist).
   */
  get video() {
    const { videos } = this;
    return videos.length ? videos[0] : undefined;
  }

  /**
   * Whether this embed represents a playlist or not.
   *
   * @return {boolean} True if a playlist, or false otherwise.
   */
  get isPlaylist() {
    return this.mediaType === 'playlist';
  }

  /**
   * Return the playlist type code associated with this embed,
   * if it's a playlist.
   */
  get playlistType() {
    if (!this.isPlaylist) {
      return null;
    }
    return get(this.attributes, 'media.type');
  }

  /**
   * Whether this embed represents a dynamic playlist (irrespective
   * of single video embeds with related videos.
   */
  get isDynamicPlaylist() {
    return this.isPlaylist && this.playlistType === PLAYLIST_DYNAMIC_TYPE;
  }

  /**
   * Given a video, indicate whether it is disabled.
   *
   * @param {Object} video - A Rover video object.
   * @return {boolean} True if the video is disabled, or false otherwise.
   */
  isVideoDisabled(video) {
    return get(video, 'attributes.is_disabled');
  }

  /**
   * Given a video, return its expiration date, if any.
   *
   * @param {Object} video - A Rover video object.
   * @return {Date} The expiration date of the video, or undefined if not set.
   */
  getExpirationDate(video) {
    const publishedTo = get(video, 'attributes.publish_to');
    if (!publishedTo) {
      return undefined;
    }
    return new Date(publishedTo);
  }

  /**
   * Given a video, return the date it becomes published, if set.
   *
   * @param {Object} video - A Rover video object.
   * @return {Date} The publication date of the video, or undefined if not set.
   */
  getPublicationDate(video) {
    const publishedFrom = get(video, 'attributes.publish_from');
    if (!publishedFrom) {
      return undefined;
    }
    return new Date(publishedFrom);
  }

  /**
   * Given a video, indicate whether it is currently expired.
   *
   * @param {Object} video - A Rover video object.
   * @param {Date} asOfDate - An optional as-of date override (default: now).
   * @return {boolean} True if the video is expired, or false otherwise.
   */
  isVideoExpired(video, asOfDate) {
    const asOfDate_ = asOfDate || new Date();
    const expirationDate = this.getExpirationDate(video);
    if (!expirationDate) {
      return false;
    }
    return asOfDate_ > expirationDate;
  }

  /**
   * Given a video, indicate whether it has been published.
   *
   * @param {Object} video - A Rover video object.
   * @param {Date} asOfDate - An optional as-of date override (default: now).
   * @return {boolean} True if the video is published, or false otherwise.
   */
  isVideoPublished(video, asOfDate) {
    const asOfDate_ = asOfDate || new Date();
    const publicationDate = this.getPublicationDate(video);
    if (!publicationDate) {
      return true;
    }
    return asOfDate_ > publicationDate;
  }

  /**
   * Given a video, return indicate whether it is currently viewable.
   *
   * @param {Object} video - A Rover video object.
   * @param {Date} asOfDate - An optional as-of date override (default: now).
   * @return {boolean} True if video is available for viewing, otherwise false.
   */
  isVideoViewable(video, asOfDate) {
    if (this.isVideoDisabled(video)) {
      return false;
    }
    const asOfDate_ = asOfDate || new Date();
    if (this.isVideoExpired(video, asOfDate_)) {
      return false;
    }
    return this.isVideoPublished(video, asOfDate_);
  }

  /**
   * The role of this embed.
   *
   * @return {number} The embed role code (ID).
   */
  get role() {
    return get(this.attributes, 'role');
  }
}
